import React, { useState, useRef, useEffect } from "react";

export default function YearPicker({
  placeholder,
  value,
  onChange,
  className,
  style,
  required = false,
  minYear = 1951,
  dir = "ltr", // 'ltr' or 'rtl'
  ...props
}) {
  const currentYear = new Date().getFullYear() - 15;
  const disabledUntil = currentYear - 1; // inclusive
  const yearsPerPage = 12;
  const columns = 4;

  // Calculate initial page based on value or default to last page
  const getInitialPage = () => {
    const start = currentYear;
    const end = minYear;
    const totalYears = start - end + 1;
    const totalPages = Math.ceil(totalYears / yearsPerPage);
    if (value && typeof value === "number") {
      const offset = start - value;
      return Math.floor(offset / yearsPerPage);
    }
    return 0;
  };

  const [page, setPage] = useState(getInitialPage());
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Navigation helpers
  const totalYears = currentYear - minYear + 1;
  const totalPages = Math.ceil(totalYears / yearsPerPage);

  // Helper to get years for a page (ascending order)
  const getYearsForPage = (pageNum) => {
    const endYear = currentYear - pageNum * yearsPerPage;
    let startYear = endYear - yearsPerPage + 1;
    if (startYear < minYear) startYear = minYear;
    const years = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }
    while (years.length < yearsPerPage) {
      years.push(null);
    }
    return years;
  };

  // Helper to check if a page has at least one enabled year
  const pageHasEnabledYear = (pageNum) => {
    const years = getYearsForPage(pageNum);
    return years.some((y) => y !== null && y <= disabledUntil);
  };

  // Find the next page with enabled years in a given direction
  const findNextEnabledPage = (startPage, direction) => {
    let p = startPage;
    while (p >= 0 && p < totalPages && !pageHasEnabledYear(p)) {
      p += direction;
    }
    if (p >= 0 && p < totalPages) return p;
    return startPage; // fallback to original if none found
  };

  // When opening, skip to first page with enabled years
  useEffect(() => {
    if (open) {
      const enabledPage = findNextEnabledPage(page, 1);
      if (enabledPage !== page) setPage(enabledPage);
    }
    // eslint-disable-next-line
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Calculate the range for the current page (ascending)
  const endYear = currentYear - page * yearsPerPage;
  let startYear = endYear - yearsPerPage + 1;
  if (startYear < minYear) startYear = minYear;

  // Generate years for the current page (ascending)
  const years = [];
  for (let y = startYear; y <= endYear; y++) {
    years.push(y);
  }
  while (years.length < yearsPerPage) {
    years.push(null);
  }

  // Navigation
  const canPrev = page < totalPages - 1;
  const canNext = page > 0;

  // Styles
  const borderStyle = "border border-gray-300 rounded-md";
  const textStyle = "text-gray-400";
  const gridStyle = "grid grid-cols-4 gap-2";
  const dirStyle = dir === "rtl" ? "rtl" : "ltr";
  

  // Navigation handlers with skip logic
  const handlePrev = (e) => {
    e.stopPropagation();
    let nextPage = page + 1;
    nextPage = findNextEnabledPage(nextPage, 1);
    if (nextPage < totalPages) setPage(nextPage);
  };
  const handleNext = (e) => {
    e.stopPropagation();
    let nextPage = page - 1;
    nextPage = findNextEnabledPage(nextPage, -1);
    if (nextPage >= 0) setPage(nextPage);
  };

  return (
    <div
      className={`flex flex-col relative min-w-[175px] ${className ?? ""}`}
      style={style}
      dir={dirStyle}
      ref={containerRef}
    >
      {/* Input/Placeholder */}
      <div
        className={`relative ${borderStyle} px-3 bg-white cursor-pointer select-non min-h-[30px]`}
        style={{ 
          display: 'flex',
          alignItems: 'center',
          flexDirection: dir === "rtl" ? "row-reverse" : "row",
          direction: dir,
          textAlign: dir === "rtl" ? "right" : "left"
        }}
        onClick={() => setOpen((v) => !v)}
        tabIndex={0}
        {...props}
      >
        {dir === "rtl" ? (
          <>
            <span className="mx-1.5 text-gray-400 text-xs flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="inline-block align-middle"
              >
                <rect x="3" y="5" width="18" height="16" rx="2" strokeWidth="2" />
                <path strokeWidth="2" d="M16 3v4M8 3v4M3 9h18" />
              </svg>
            </span>
            <div className="flex-1 text-right">
              {!value ? (
                <span className={`text-gray-400 font-heebo text-sm`}>
                  {placeholder}                  {required && <span className="text-red-500">*</span>}
                </span>
              ) : (
                <span className="text-gray-700 font-heebo text-sm">{value}</span>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 text-left">
              {!value ? (
                <span className={`text-gray-400 font-heebo text-sm`}>
                  {placeholder}                  {required && <span className="text-red-500">*</span>}
                </span>
              ) : (
                <span className="text-gray-700 font-heebo text-sm">{value}</span>
              )}
            </div>
            <span className="mx-1.5 text-gray-400 text-xs flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="inline-block align-middle"
              >
                <rect x="3" y="5" width="18" height="16" rx="2" strokeWidth="2" />
                <path strokeWidth="2" d="M16 3v4M8 3v4M3 9h18" />
              </svg>
            </span>
          </>
        )}
      </div>
      {/* Popover Year Grid */}
      {open && (
        <div
          className={`absolute z-10 mt-1 bg-white shadow-lg border border-gray-200 rounded-md p-3 ${
            dir === "rtl" ? "right-0" : "left-0"
          }`}
          style={{ minWidth: 280, maxHeight: 300 }}
        >
          {/* Header */}
          <div className={`flex items-center justify-between mb-2 border-b border-gray-200 pb-2 ${dir === "rtl" ? "rtl:flex-row-reverse" : ""}`}>
            <button
              type="button"
              onClick={handlePrev}
              disabled={!canPrev}
              className="px-2 py-1 text-3xl text-gray-400 hover:text-gray-600 disabled:opacity-30"
              aria-label="Previous years"
            >
              {dir === "ltr" ? "«" : "»"}
            </button>
            <span className="font-medium font-heebo text-gray-500 text-base">
              {startYear} - {endYear}
            </span>
            <button
              type="button"
              onClick={handleNext}
              disabled={!canNext}
              className="px-2 py-1 text-3xl text-gray-400 hover:text-gray-600 disabled:opacity-30"
              aria-label="Next years"
            >
              {dir === "ltr" ? "»" : "«"}
            </button>
          </div>
          {/* Grid of years */}
          <div className={gridStyle} style={{ direction: dirStyle }}>
            {years.map((year, idx) =>
              year ? (
                <button
                  key={year}
                  type="button"
                  className={`py-1 w-fit px-4 rounded text-center ${textStyle} ${
                    year > disabledUntil
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-100 text-gray-700 font-heebo"
                  } ${
                    value === year ? "bg-blue-100 border-blue-400 border" : ""
                  }`}
                  disabled={year > disabledUntil}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (year <= disabledUntil && onChange) {
                      onChange(year);
                      setOpen(false);
                    }
                  }}
                >
                  {year}
                </button>
              ) : (
                <span key={"empty-" + idx}></span>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
